import React, { useEffect, useRef, useState } from 'react';
import './rules.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  getPaginationOpts,
  getResponseError,
  responseLogWithNotification,
  useFilterForm,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getRulesData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import RulesDTO from '../../../dto/RulesDTO';
import RulesForm from './RulesForm';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm } from '../../System/actions';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import FilterForm from './FilterForm';
import { rulesAdditionalColumns, rulesColumns } from './rulesColumns';
import {
  deleteRules, loadRulesById, loadRulesByIdClear, loadRulesList,
} from '../actions';
import { RULES_MODULE } from '../../../configs/moduleNames';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import TextInput from '../../../components/fields/TextInput/TextInput';

export default function RulesPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);

  const store = useStore();
  const {
    rulesListMeta,
    loadingRulesList,
    rulesList,
    loadingCurrentRules,
    currentRules,
    loadingCreateRules,
    loadingEditRulesById,
    loadingDeleteRules,
  } = useSelector(getRulesData);
  const { formatMessage } = useIntl();

  const loadRules = id => {
    if (id) {
      dispatch(loadRulesById(id))
        .then(() => setShowForm(true))
        .catch(response => errorNotification('Загрузка правила', getResponseError(response)));
    }
  };

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[RULES_MODULE].rulesListMeta, opts); // костыль внедрен из за потери контекста в updatePeriod. Он сохранял первую функцию
    const requestData = {
      ...paginationOpts,
      filter: getClearValues(),
    };
    const p1 = dispatch(loadRulesList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const {
    getValues, getClearValues, control, setValue, reset,
  } = useFilterForm({}, loadData, ['rules_id']);
  const vid = getValues('rules_id');
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  React.useEffect(() => loadRules(getValues('rules_id')), [vid]);
  const DTO = new RulesDTO();
  const attributes = DTO.getAttributesByName();

  const handleSelectRow = item => {
    let select = item;
    if (selectedRow && selectedRow.rule_id === item.rule_id) {
      select = null;
    }
    setSelectedRow(select);
  };

  const handleCreate = () => {
    setIsEdit(false);
    setShowView(false);
    setShowForm(true);
    clearCurrentState();
  };

  const handleEdit = item => event => {
    if (!item) {
      return;
    }
    setValue('rules_id', item.rule_id);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDelete = item => event => {
    if (!item.rule_id) {
      errorNotification('Ошибка', 'Нет ID');
    }
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление правила # ${item.rule_id}`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteRules(item.rule_id)).then(() => {
        if (selectedRow === item.rule_id) {
          setSelectedRow(null);
        }
        loadData();
        successNotification('Удаление правила', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление правила', getResponseError(response));
      });
    }).catch(() => {});
    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  const copyLink = item => event => {
    navigator.clipboard.writeText(`${window.location.href}?rules_id=${item.rule_id}`);
    successNotification('Копирование ссылки на правило', 'Ссылка на просмотр скопирована.');
  };

  const handleClose = () => {
    setShowForm(false);
    setValue('rules_id', '');
    clearCurrentState();
  };

  const clearCurrentState = () => {
    dispatch(loadRulesByIdClear()).catch(responseLogWithNotification);
  };

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={(
        <div className="more-wrapper">
          <Button variant="link" className="w-100 more-button" onClick={copyLink(item)}>
            Копировать ссылку
          </Button>
          <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}>
            Просмотреть
          </Button>
          <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
            Удалить
          </Button>
        </div>
      )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    if (selectedRow && item.rule_id === selectedRow.rule_id) {
      classes.push('selected');
    }
    return classes.join(' ');
  };

  return (
    <div className="page application-page">
      <Loader loading={loadingRulesList || loadingCurrentRules || loadingDeleteRules || loadingCreateRules || loadingEditRulesById} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.rules' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextInput}
            dtoObject={attributes.name}
            hideSmallButtons
            showArrowIcon
            usePortal
            placeholder="Название"
            className="no-border"
          />
          {/* <div className="filter-sep" /> */}
          {/* <Button variant="second" onClick={() => setOpenFilter(true)}> */}
          {/*  <Icons customIcon width={24} height={24} icon="filter" /> */}
          {/* </Button> */}
          <div className="filter-sep" />
          <Button variant="second" onClick={handleCreate}>
            <Icons
              customIcon
              width={24}
              height={24}
              icon="plus"
            />
          </Button>
          {isOpenFilter
            ? (
              <FilterForm
                control={control}
                onHide={() => {
                  setOpenFilter(false);
                }}
                getValues={getValues}
                reset={reset}
                defaultValues={{}}
                setValue={setValue}
              />
            )
            : null}
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                onClickByRow={handleSelectRow}
                onDoubleClickByRow={(item, event) => handleEdit(item)(event)}
                rowClassFormatter={rowClassFormatter}
                data={rulesList}
                columns={rulesColumns}
                additionalColumns={rulesAdditionalColumns}
                name="rules-list"
                settingsFormatter={moreFormatter}
              />
            </div>
          </div>
          <Pagination meta={rulesListMeta} list={rulesList} handleChangeMeta={loadData} />
        </div>
      </div>
      {showForm ? (
        <RulesForm
          currentRules={currentRules}
          isEdit={isEdit}
          loadData={loadData}
          onClose={handleClose}
          loadRules={loadRules}
        />
      ) : null}
    </div>
  );
}
